import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { cutoffParam, useChangesByIds, useChangesInView, useDataReviewSummary, useJobGroups } from "../../api/dataReview";
import { useProject } from "../../api/project";
import { useVegetationTypes } from "../../api/vegetationType";
import { useDataReview } from "../../contexts/DataReviewContext";
import { DataChangeFeature, DataReviewJob, JobChangesSummary } from "../../types/dataReview";
import { TARGET_COLOURS } from "../layer_components/DataReviewLayer";
import { boundaryFeature, geometryAreaM2, GroupStats, toGroups } from "./editorModel";
import { useVegEditor } from "./VegEditorContext";

export const jobBusy = (j: DataReviewJob | null | undefined) => !!j && (j.status === "queued" || j.status === "running");
/** A run that can be picked up again: parsed or parsing, not yet applied. */
export const jobOpen = (j: DataReviewJob | null | undefined) => !!j && (j.status === "queued" || j.status === "running" || j.status === "review");
/** Queued while the machine that runs the model starts up (older backends call it gpu_starting). */
export const workerStarting = (j: DataReviewJob | null | undefined) => !!(j?.worker_starting ?? j?.gpu_starting);

/** Everything the editor reads: the review summary, the run it is in,
 *  that run's findings counted per target, the shapes in the map's view,
 *  the shape in hand, the project's classes and colours, and what applying
 *  would do. One hook so every panel sees the same numbers.
 *
 *  A run can hold tens of thousands of findings, so they are never fetched
 *  whole: the server counts them, orders one target at a time, and sends
 *  the shapes in view. */
export function useEditorData(active = true) {
  const { projectId } = useParams<{ projectId: string }>();
  const { sessionId, remainder, view, phase } = useVegEditor();
  const { passThresholds, currentId } = useDataReview();
  const { data: summaryRes } = useDataReviewSummary(projectId);
  const summary = summaryRes?.data;
  const detailArea = summary?.areas.find((a) => a.key === "detail");
  const vegArea = summary?.areas.find((a) => a.key === "vegetation");
  const catalogue = detailArea?.terms;
  const jobs = useMemo(() => summary?.jobs ?? [], [summary?.jobs]);
  const job = useMemo(() => jobs.find((j) => j.id === sessionId) ?? null, [jobs, sessionId]);
  const resumable = useMemo(() => jobs.find(jobOpen) ?? null, [jobs]);
  // Only while a refinement is open; the project page calls this for the
  // layer colours and leaves the findings alone otherwise.
  const inSession = active && phase === "session" && !!job;
  const sessionProject = inSession ? projectId : undefined;
  // Cut-offs the slider has moved but not yet saved go with every request.
  const thr = cutoffParam(job?.id, passThresholds);
  const { data: groupsRes, dataUpdatedAt: groupsUpdatedAt } = useJobGroups(sessionProject, job?.id, thr);
  const groups: Record<string, GroupStats> = useMemo(() => toGroups(groupsRes?.data?.groups, catalogue), [groupsRes, catalogue]);
  // The shapes in view, above the cut-off and not rejected.
  const { data: viewRes } = useChangesInView(sessionProject, job?.id, view, thr);
  const features: DataChangeFeature[] = useMemo(() => viewRes?.data?.features ?? [], [viewRes]);
  const viewTruncated = !!viewRes?.data?.truncated;
  // The shape in hand, in full, wherever it is.
  const currentIds = useMemo(() => (currentId != null ? [currentId] : []), [currentId]);
  const { data: currentRes, isFetched: currentFetched, isPlaceholderData: currentPlaceholder } = useChangesByIds(sessionProject, job?.id, currentIds);
  const current: DataChangeFeature | null =
    currentRes?.data?.features?.find((f) => f.properties.id === currentId) ?? features.find((f) => f.properties.id === currentId) ?? null;
  /** The shape in hand no longer exists (merged, erased, discarded). */
  const currentGone = currentId != null && currentFetched && !currentPlaceholder && !current;
  const { project } = useProject();
  const projectData = project?.data;
  const classes: string[] = projectData?.veg_classes ?? [];
  const { data: vegTypesRes } = useVegetationTypes();
  const colours = useMemo(() => {
    const out: Record<string, string> = { ...TARGET_COLOURS };
    for (const c of vegArea?.class_areas ?? []) out[c.veg_class] = c.color;
    for (const t of vegTypesRes?.data ?? []) if (t.color) out[t.veg_class] = t.color;
    return out;
  }, [vegArea?.class_areas, vegTypesRes]);
  const boundary = useMemo(() => boundaryFeature(projectData?.boundary as GeoJSON.Geometry | null | undefined), [projectData?.boundary]);
  const projectAreaM2 = useMemo(() => geometryAreaM2(boundary?.geometry), [boundary]);
  const isClean = job?.options?.start === "clean";
  /** The wand reads the run's own raster, which exists once the image is prepared. */
  const hasWand = !!job?.stats?.wand_key;
  const paintOps = Number(job?.stats?.paint_ops ?? 0);
  const maxAreaM2 = detailArea?.max_area_m2 ?? 4_000_000;
  const parsed = !!job?.stats?.rgb_key;
  const preparing = jobBusy(job) && !parsed;
  const runningPass = jobBusy(job) && parsed;
  const jobArea = job?.area ? ({ type: "Feature", properties: {}, geometry: job.area } as GeoJSON.Feature) : null;
  const jobAreaM2 = useMemo(() => geometryAreaM2(job?.area), [job?.area]);
  // What the run adds and what is left unmapped, from the server's totals
  // of the accepted findings. Nothing reads the vegetation layer until the
  // run is applied.
  const accepted = groupsRes?.data?.accepted;
  const jobSummary: JobChangesSummary | null = useMemo(() => {
    if (!job) return null;
    const rem = remainder ?? job.options?.remainder ?? null;
    const mode = rem?.mode ?? null;
    const areaM2 = Number(job.stats?.area_m2 ?? jobAreaM2) || 0;
    const kept = isClean && job.options?.keep_buildings !== false ? Number(job.stats?.buildings_m2 ?? 0) : 0;
    const ground = Math.max(0, areaM2 - kept);
    const adds: Record<string, number> = { ...(accepted?.by_target ?? {}) };
    const covered = Math.min(accepted?.covered_m2 ?? 0, ground);
    const wipes = isClean && mode !== "orig";
    let unmapped = wipes ? Math.max(0, ground - covered) : 0;
    if (wipes && mode === "class" && rem?.veg_class && unmapped > 0) { adds[rem.veg_class] = (adds[rem.veg_class] ?? 0) + unmapped; unmapped = 0; }
    return {
      rows: Object.entries(adds).filter(([, v]) => v >= 0.5).sort((a, b) => b[1] - a[1]).map(([veg_class, area_m2]) => ({ veg_class, area_m2 })),
      area_m2: areaM2, buildings_m2: kept, unmapped_m2: unmapped, changed_m2: wipes ? ground : covered,
      shapes: accepted?.shapes ?? 0, painted: accepted?.painted ?? 0, assets: accepted?.assets ?? 0,
      remainder: { mode, veg_class: rem?.veg_class ?? "" }, needs_remainder: isClean && unmapped > 0.5 && mode === null,
    };
  }, [job, accepted, remainder, isClean, jobAreaM2]);
  return {
    groupsUpdatedAt, thr, current, currentGone, viewTruncated,
    projectId, summary, vegArea, catalogue, jobs, job, resumable, features, groups, classes,
    colours, boundary, projectAreaM2, jobSummary, isClean, hasWand, paintOps, maxAreaM2, preparing, runningPass, jobArea, jobAreaM2,
    applying: !!summary?.applying,
  };
}

